'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { STATUSES, STATUS_LABELS, PartnerStatus, COUNTRIES, CATEGORY_LABELS } from '@/types';
import CodesPanel from './CodesPanel';
import FlowPanel from './FlowPanel';

interface Insights {
  generatedAt: string;
  country: string;
  days: number | 'all';
  total: number;
  active: number;
  meetingSet: number;
  negotiating: number;
  negAvgProb: number | null;
  signed: number;
  passed: number;
  winRate: number | null;
  contactedNow: number;
  contactedPrev: number | null;
  noNextAction: number;
  byStatus: Record<string, number>;
  byCountry: { country: string; n: number }[];
  needsAttention: {
    partner: string;
    status: string;
    country: string;
    probability: number | null;
    days: number | null;
  }[];
  followUpsDue: {
    partner: string;
    status: string;
    country: string;
    priority: string | null;
    category: string;
    probability: number | null;
    next_action: string;
    days: number | null;
  }[];
  dailyActivity: { day: string; nuevos: number; follows: number }[];
  closings: { day: string; partners: string[] }[];
  lists: {
    meeting_set: { partner: string; country: string; probability: number | null }[];
    negotiating: { partner: string; country: string; probability: number | null }[];
    signed: { partner: string; country: string; probability: number | null }[];
  };
}

interface Activity {
  id: string;
  action_type: string;
  notes: string | null;
  created_at: string;
  partner: string;
  country: string;
}

const ACTION_META: Record<string, { icon: string; label: string }> = {
  note: { icon: '📝', label: 'Nota' },
  email: { icon: '✉️', label: 'Correo' },
  meeting: { icon: '📅', label: 'Reunión' },
  call: { icon: '📞', label: 'Llamada' },
  intro: { icon: '🤝', label: 'Conexión' },
  stage_change: { icon: '🔀', label: 'Cambio de etapa' },
};

const DATE_PRESETS = [
  { value: '7', label: 'Últimos 7 días' },
  { value: '30', label: 'Últimos 30 días' },
  { value: '90', label: 'Últimos 90 días' },
  { value: 'all', label: 'Todo el tiempo' },
];

function timeAgo(iso: string): string {
  const t = new Date(iso.replace(' ', 'T') + 'Z').getTime();
  if (Number.isNaN(t)) return iso.slice(0, 16);
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return 'ahora';
  if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  if (d < 30) return `hace ${d} d`;
  return iso.slice(0, 10);
}

const NUEVO_COLOR = '#0890F1';
const FOLLOW_COLOR = '#F59E0B';
const CERRADO_COLOR = '#22C55E';

function dayTick(day: string): string {
  const today = new Date();
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const yest = new Date(today);
  yest.setDate(today.getDate() - 1);
  if (day === iso(today)) return 'Hoy';
  if (day === iso(yest)) return 'Ayer';
  const d = new Date(day + 'T00:00:00');
  return d.toLocaleDateString('es-PE', { day: 'numeric', month: 'short' }).replace('.', '');
}

function DailyActivity({
  rows,
  closings,
  signedTotal,
}: {
  rows: { day: string; nuevos: number; follows: number }[];
  closings: { day: string; partners: string[] }[];
  signedTotal: number;
}) {
  const asc = [...rows].reverse(); // más viejo → más nuevo (izquierda → derecha)
  const totalNuevos = rows.reduce((s, r) => s + r.nuevos, 0);
  const totalFollows = rows.reduce((s, r) => s + r.follows, 0);
  const today = asc.length ? asc[asc.length - 1] : { nuevos: 0, follows: 0 };
  const maxY = Math.max(1, ...asc.map((r) => Math.max(r.nuevos, r.follows)));

  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(720);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const padL = 8;
  const padR = 8;
  const plotH = 132;
  const labelH = 18;
  const n = asc.length;
  const innerW = Math.max(1, w - padL - padR);
  const x = (i: number) => padL + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const step = Math.max(1, Math.ceil(n / 10));
  const idxByDay = new Map(asc.map((r, i) => [r.day, i]));

  // Layout de hitos de cierre: una etiqueta por alianza (con nombre), cada una en
  // la fila más baja donde no choca con otra (cierres el mismo día o días seguidos).
  const CHAR_W = 5.2; // ancho aprox. por carácter a 9px bold
  const rowRight: number[] = [];
  const closeLayout = closings
    .map((c) => ({ c, i: idxByDay.get(c.day) }))
    .filter((o): o is { c: (typeof closings)[number]; i: number } => o.i !== undefined)
    .sort((a, b) => a.i - b.i)
    .map(({ c, i }) => {
      const cx = x(i);
      const when = ` · ${dayTick(c.day)}`;
      const labels = c.partners.map((name) => {
        const lw = (name.length + when.length) * CHAR_W;
        const left = Math.max(padL, Math.min(cx - lw / 2, padL + innerW - lw));
        let row = rowRight.findIndex((r) => left > r + 8);
        if (row === -1) row = rowRight.length;
        rowRight[row] = left + lw;
        return { name, left, row };
      });
      return { day: c.day, partners: c.partners, cx, labels };
    });
  const closedInWindow = closeLayout.reduce((s, c) => s + c.partners.length, 0);
  const lastDay = asc.length ? asc[asc.length - 1].day : '';
  const closedToday = closeLayout.find((c) => c.day === lastDay)?.partners ?? [];

  const padT = 27 + Math.max(1, rowRight.length - 1) * 11; // espacio arriba para los hitos de cierre
  const H = padT + plotH + labelH;
  const y = (v: number) => padT + plotH - (v / maxY) * plotH;
  const linePath = (key: 'nuevos' | 'follows') =>
    asc.map((r, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(r[key]).toFixed(1)}`).join(' ');

  const LegendLine = ({ color, n: num, label }: { color: string; n: number; label: string }) => (
    <span className="flex items-center gap-1.5">
      <svg width="16" height="8">
        <line x1="0" y1="4" x2="16" y2="4" stroke={color} strokeWidth={2} />
        <circle cx="8" cy="4" r="2.6" fill="#fff" stroke={color} strokeWidth={1.6} />
      </svg>
      <span className="text-gray-500">
        {num} <span className="text-gray-400">{label}</span>
      </span>
    </span>
  );

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm mb-6">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <h2 className="text-sm font-bold text-gray-700">📊 Actividad por día</h2>
        <span className="text-xs text-gray-400">nuevos · follows · 🟢 cierre</span>
        <div className="ml-auto flex items-center gap-3 text-xs">
          <LegendLine color={NUEVO_COLOR} n={totalNuevos} label="nuevos" />
          <LegendLine color={FOLLOW_COLOR} n={totalFollows} label="follows" />
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CERRADO_COLOR }} />
            <span className="text-gray-500">
              {closedInWindow} <span className="text-gray-400">cierres · {signedTotal} en total</span>
            </span>
          </span>
        </div>
      </div>
      {/* Resumen de HOY, bien explícito */}
      <div className="flex items-center gap-2 mb-4 text-sm flex-wrap">
        <span className="font-bold text-gray-700">Hoy:</span>
        <span className="px-2.5 py-1 rounded-full font-bold" style={{ backgroundColor: `${NUEVO_COLOR}1A`, color: NUEVO_COLOR }}>
          {today.nuevos} nuevos
        </span>
        <span className="px-2.5 py-1 rounded-full font-bold" style={{ backgroundColor: `${FOLLOW_COLOR}1A`, color: FOLLOW_COLOR }}>
          {today.follows} follows
        </span>
        <span className="text-xs text-gray-400">= {today.nuevos + today.follows} toques</span>
        {closedToday.length > 0 && (
          <span className="px-2.5 py-1 rounded-full font-bold" style={{ backgroundColor: `${CERRADO_COLOR}1A`, color: CERRADO_COLOR }}>
            🟢 Cerraste {closedToday.join(' y ')}
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 py-2">Sin toques salientes en este período.</p>
      ) : (
        <div ref={ref} className="w-full">
          <svg width={w} height={H} className="block">
            {[0, 0.5, 1].map((f) => {
              const gy = padT + plotH - f * plotH;
              return <line key={f} x1={padL} x2={padL + innerW} y1={gy} y2={gy} stroke="#EEF1F5" strokeWidth={1} />;
            })}
            {/* hitos de cierre: marcador verde el día que una alianza cerró */}
            {closeLayout.map((c) => (
              <g key={'c' + c.day}>
                <line x1={c.cx} x2={c.cx} y1={padT} y2={padT + plotH} stroke={CERRADO_COLOR} strokeWidth={1} strokeDasharray="3 3" opacity={0.45} />
                <circle cx={c.cx} cy={padT - 8} r={4} fill={CERRADO_COLOR} stroke="#fff" strokeWidth={1.5}>
                  <title>{`Cierre (${dayTick(c.day)}): ${c.partners.join(', ')}`}</title>
                </circle>
                {c.labels.map((l) => (
                  <text key={l.name} x={l.left} y={padT - 16 - l.row * 11} fontSize={9} fontWeight={700} fill={CERRADO_COLOR}>
                    {l.name}
                    <tspan fontWeight={400}> · {dayTick(c.day)}</tspan>
                  </text>
                ))}
              </g>
            ))}
            <path d={linePath('follows')} fill="none" stroke={FOLLOW_COLOR} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            <path d={linePath('nuevos')} fill="none" stroke={NUEVO_COLOR} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            {asc.map((r, i) => (
              <g key={r.day}>
                <circle cx={x(i)} cy={y(r.follows)} r={2.6} fill="#fff" stroke={FOLLOW_COLOR} strokeWidth={1.6}>
                  <title>{`${dayTick(r.day)}: ${r.follows} follows`}</title>
                </circle>
                <circle cx={x(i)} cy={y(r.nuevos)} r={2.6} fill="#fff" stroke={NUEVO_COLOR} strokeWidth={1.6}>
                  <title>{`${dayTick(r.day)}: ${r.nuevos} nuevos`}</title>
                </circle>
              </g>
            ))}
            {asc.map((r, i) =>
              i % step === 0 || i === n - 1 ? (
                <text key={'t' + r.day} x={x(i)} y={H - 4} fontSize={9} textAnchor="middle" fill="#9CA3AF">
                  {dayTick(r.day)}
                </text>
              ) : null
            )}
          </svg>
        </div>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  accent,
  onClick,
  active,
}: {
  label: string;
  value: string | number;
  sub?: React.ReactNode;
  accent?: string;
  onClick?: () => void;
  active?: boolean;
}) {
  const clickable = !!onClick;
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-2xl p-4 border shadow-sm transition ${
        clickable ? 'cursor-pointer hover:border-[#0890F1]/40 hover:shadow' : ''
      } ${active ? 'border-[#0890F1] ring-1 ring-[#0890F1]/20' : 'border-gray-100'}`}
    >
      <p className="text-xs font-medium text-gray-400 mb-1 flex items-center gap-1">
        {label}
        {clickable && <span className="text-[#0890F1] text-[10px]">{active ? '▲' : '▼'}</span>}
      </p>
      <p className="text-2xl font-extrabold leading-none" style={{ color: accent }}>
        {value}
      </p>
      {sub && <p className="text-xs text-gray-500 mt-1.5">{sub}</p>}
    </div>
  );
}

const selectClass =
  'text-sm px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:border-gray-300 cursor-pointer focus:outline-none focus:border-[#0890F1]';

export default function InsightsDashboard() {
  const [ins, setIns] = useState<Insights | null>(null);
  const [acts, setActs] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [fCountry, setFCountry] = useState('all');
  const [fDays, setFDays] = useState('30');
  const [tab, setTab] = useState<'pipeline' | 'flow' | 'codes'>('pipeline');

  const load = useCallback(async () => {
    try {
      const qs = `?country=${encodeURIComponent(fCountry)}&days=${fDays}`;
      const [ri, ra] = await Promise.all([fetch('/api/insights' + qs), fetch('/api/activity' + qs)]);
      if (!ri.ok || !ra.ok) throw new Error(`HTTP ${ri.status}/${ra.status}`);
      setIns(await ri.json());
      setActs(await ra.json());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [fCountry, fDays]);

  useEffect(() => {
    load();
  }, [load]);

  const daysLabel = fDays === 'all' ? 'total' : `${fDays} días`;

  const tabs = (
    <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
      {(
        [
          ['pipeline', '📊 Pipeline'],
          ['flow', '🧭 Flujo'],
          ['codes', '🎟️ Códigos'],
        ] as const
      ).map(([k, label]) => (
        <button
          key={k}
          onClick={() => setTab(k)}
          className={`text-sm px-3 py-2 -mb-px border-b-2 transition ${
            tab === k ? 'border-[#0890F1] text-[#0890F1] font-semibold' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (tab !== 'pipeline') {
    return (
      <div className="overflow-y-auto pb-6">
        {tabs}
        {tab === 'codes' ? <CodesPanel /> : <FlowPanel />}
      </div>
    );
  }

  return (
    <div className="overflow-y-auto pb-6">
      {tabs}
      {/* Filtros */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select value={fCountry} onChange={(e) => setFCountry(e.target.value)} className={selectClass}>
          <option value="all">🌎 Total (todos los países)</option>
          {COUNTRIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select value={fDays} onChange={(e) => setFDays(e.target.value)} className={selectClass}>
          {DATE_PRESETS.map((d) => (
            <option key={d.value} value={d.value}>
              📅 {d.label}
            </option>
          ))}
        </select>
        {loading && <span className="text-xs text-gray-400">actualizando…</span>}
      </div>

      {error && !ins ? (
        <div className="flex items-center justify-center h-64 text-red-600">{error}</div>
      ) : !ins ? (
        <div className="flex items-center justify-center h-64 text-gray-400">Cargando insights…</div>
      ) : (
        <Body ins={ins} acts={acts} daysLabel={daysLabel} />
      )}
    </div>
  );
}

function Body({ ins, acts, daysLabel }: { ins: Insights; acts: Activity[]; daysLabel: string }) {
  const delta = ins.contactedPrev != null ? ins.contactedNow - ins.contactedPrev : null;
  const funnelMax = Math.max(1, ...STATUSES.map((s) => ins.byStatus[s.id] ?? 0));
  const [openList, setOpenList] = useState<null | 'meeting_set' | 'negotiating' | 'signed'>(null);
  const toggle = (k: 'meeting_set' | 'negotiating' | 'signed') => setOpenList((o) => (o === k ? null : k));

  return (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-3">
        <Kpi label="Pipeline activo" value={ins.active} sub={`de ${ins.total} en total`} />
        <Kpi
          label="Reunión agendada"
          value={ins.meetingSet}
          accent="#A855F7"
          sub="call agendada · pre-firma"
          onClick={() => toggle('meeting_set')}
          active={openList === 'meeting_set'}
        />
        <Kpi
          label="En negociación"
          value={ins.negotiating}
          accent="#F59E0B"
          sub={ins.negAvgProb != null ? `${ins.negAvgProb}% prob. promedio` : '—'}
          onClick={() => toggle('negotiating')}
          active={openList === 'negotiating'}
        />
        <Kpi
          label="Cerrados / activos"
          value={ins.signed}
          accent="#22C55E"
          sub={`alianzas corriendo · ${ins.passed} descartados`}
          onClick={() => toggle('signed')}
          active={openList === 'signed'}
        />
        <Kpi
          label="Win rate"
          value={ins.winRate != null ? `${ins.winRate}%` : '—'}
          sub="cerrados / (cerrados + descartados)"
        />
        <Kpi
          label={`Contactados (${daysLabel})`}
          value={ins.contactedNow}
          accent="#0890F1"
          sub={
            delta != null ? (
              <span className={delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-500' : 'text-gray-400'}>
                {delta > 0 ? '▲' : delta < 0 ? '▼' : '='} {delta >= 0 ? '+' : ''}
                {delta} vs. período previo
              </span>
            ) : (
              'histórico'
            )
          }
        />
      </div>

      {/* Lista desplegable al hacer clic en un KPI */}
      {openList && (
        <div className="bg-white rounded-2xl p-4 border border-[#0890F1]/30 shadow-sm mb-6">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-sm font-bold text-gray-700">
              {openList === 'meeting_set'
                ? '🟣 Reunión agendada'
                : openList === 'negotiating'
                  ? '🟠 En negociación'
                  : '🟢 Cerrados / activos'}
            </h3>
            <span className="text-xs text-gray-400">{ins.lists[openList].length}</span>
            <button onClick={() => setOpenList(null)} className="ml-auto text-xs text-gray-400 hover:text-gray-600">
              cerrar ✕
            </button>
          </div>
          {ins.lists[openList].length === 0 ? (
            <p className="text-sm text-gray-400">Ninguno.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-0.5">
              {ins.lists[openList].map((p) => (
                <div key={p.partner + p.country} className="flex items-center gap-2 py-1 border-b border-gray-50">
                  <span className="text-sm text-gray-800 truncate flex-1">{p.partner}</span>
                  <span className="text-xs text-gray-400 flex-shrink-0">{p.country}</span>
                  {p.probability != null && (
                    <span className="text-xs font-semibold text-amber-600 w-9 text-right flex-shrink-0">{p.probability}%</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Actividad por día — qué se hizo, nuevos vs follows */}
      <DailyActivity rows={ins.dailyActivity} closings={ins.closings} signedTotal={ins.signed} />

      {/* Radar de follow-ups — el driver del día */}
      <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm mb-6 ring-1 ring-[#0890F1]/15">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-sm font-bold text-gray-700">🔔 Radar de follow-ups</h2>
          <span className="text-xs text-gray-400">listos para 2º toque · 6–13 días de silencio</span>
          <span className="ml-auto text-xs font-semibold text-[#0890F1] bg-[#0890F1]/10 rounded-full px-2 py-0.5">
            {ins.followUpsDue.length} hoy
          </span>
        </div>
        {ins.followUpsDue.length === 0 ? (
          <p className="text-sm text-gray-400 py-2">Nada en la ventana dulce ✅ — ningún activo pide follow-up hoy.</p>
        ) : (
          <div className="flex flex-col divide-y divide-gray-50">
            {ins.followUpsDue.map((p) => {
              const st = STATUS_LABELS[p.status as PartnerStatus];
              const cat = CATEGORY_LABELS[p.category];
              return (
                <div key={p.partner} className="flex items-center gap-3 py-2">
                  {p.priority === 'high' && <span className="text-[10px]" title="alta prioridad">🔴</span>}
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: st?.color }} title={st?.label} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-semibold text-gray-800 truncate">
                        {cat?.emoji} {p.partner}
                      </span>
                      {p.probability != null && (
                        <span className="text-[11px] text-amber-600 flex-shrink-0">{p.probability}%</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 leading-snug truncate">→ {p.next_action}</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{p.country}</span>
                  <span className="text-xs font-semibold text-[#0890F1] w-14 text-right flex-shrink-0">
                    {p.days} días
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Embudo + país + atención (izquierda) */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <h2 className="text-sm font-bold text-gray-700 mb-4">Embudo por etapa</h2>
            <div className="flex flex-col gap-2.5">
              {STATUSES.map((s) => {
                const n = ins.byStatus[s.id] ?? 0;
                return (
                  <div key={s.id} className="flex items-center gap-3">
                    <span className="w-28 text-xs text-gray-600 flex items-center gap-1.5 flex-shrink-0">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                      {s.label}
                    </span>
                    <div className="flex-1 h-5 bg-gray-100 rounded-md overflow-hidden">
                      <div
                        className="h-full rounded-md transition-all"
                        style={{ width: `${(n / funnelMax) * 100}%`, backgroundColor: s.color, minWidth: n > 0 ? 6 : 0 }}
                      />
                    </div>
                    <span className="w-8 text-right text-sm font-semibold text-gray-700 flex-shrink-0">{n}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-baseline gap-2 mb-4">
              <h2 className="text-sm font-bold text-gray-700">Por país</h2>
              <span className="text-xs text-gray-400">split total (referencia)</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {ins.byCountry.map((c) => (
                <div key={c.country} className="flex-1 min-w-[100px] bg-gray-50 rounded-xl px-4 py-3">
                  <p className="text-xs text-gray-500">{c.country}</p>
                  <p className="text-xl font-bold text-gray-800">{c.n}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-sm font-bold text-gray-700">Necesita atención</h2>
              <span className="text-xs text-gray-400">activos sin contacto +14 días</span>
              {ins.noNextAction > 0 && (
                <span className="ml-auto text-xs text-amber-600 bg-amber-50 rounded-full px-2 py-0.5">
                  {ins.noNextAction} sin próxima acción
                </span>
              )}
            </div>
            {ins.needsAttention.length === 0 ? (
              <p className="text-sm text-gray-400 py-2">Todo al día ✅ — ningún activo estancado.</p>
            ) : (
              <div className="flex flex-col divide-y divide-gray-50">
                {ins.needsAttention.map((p) => {
                  const st = STATUS_LABELS[p.status as PartnerStatus];
                  return (
                    <div key={p.partner} className="flex items-center gap-3 py-2">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: st?.color }} />
                      <span className="text-sm text-gray-800 flex-1 truncate">{p.partner}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{p.country}</span>
                      <span className="text-xs font-semibold text-red-500 w-16 text-right flex-shrink-0">
                        {p.days != null ? `${p.days} días` : 'sin tocar'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Feed (derecha) */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <h2 className="text-sm font-bold text-gray-700 mb-4">Actividad reciente</h2>
          <div className="flex flex-col gap-3">
            {acts.map((a) => {
              const meta = ACTION_META[a.action_type] ?? ACTION_META.note;
              return (
                <div key={a.id} className="flex gap-3">
                  <span className="text-base leading-none mt-0.5" title={meta.label}>
                    {meta.icon}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-semibold text-gray-800 truncate">{a.partner}</span>
                      <span className="text-[11px] text-gray-400 flex-shrink-0 ml-auto">{timeAgo(a.created_at)}</span>
                    </div>
                    {a.notes && <p className="text-xs text-gray-500 leading-snug mt-0.5 line-clamp-2">{a.notes}</p>}
                  </div>
                </div>
              );
            })}
            {acts.length === 0 && <p className="text-sm text-gray-400">Sin actividad en este período.</p>}
          </div>
        </div>
      </div>
    </>
  );
}
